import { ApiClientError, apiRequest } from "./apiClient";
import {
  BrowserAuthConfigurationError,
  authCallbackUrl,
  browserAuthMode,
  getSupabaseClient,
  subscribeToSupabaseAuth,
} from "./supabaseClient";
import { cachedQuery, invalidateQueries } from "./queryCache";

export type AuthProvider = "google" | "linkedin";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  accountState: "active" | "suspended" | "pending-verification";
  onboardingComplete: boolean;
}

export interface OAuthStart {
  redirectUrl: string;
}

export interface EmailLinkAccepted {
  accepted: true;
  developmentVerificationUrl?: string | null;
}

export interface EmailOtpAccepted {
  accepted: true;
}

export interface AuthConfiguration {
  authMode: "local" | "supabase";
  inviteOnly: boolean;
  alphaAccessUrl: string;
  emailEnabled: boolean;
  googleEnabled: boolean;
  linkedinEnabled: boolean;
  callbackPath: string;
  alphaUserLimit: number;
  alphaEvaluationLimit: number;
  alphaFeedbackUrl?: string | null;
}

export interface AlphaAdmissionStart {
  accepted: true;
  state: "seat-reserved" | "waitlist-verification";
  otpExpiresInSeconds: number;
}

export interface AlphaAdmissionComplete {
  state: "admitted" | "waitlisted" | "exhausted";
  feedbackUrl?: string | null;
}

export type AuthSessionEvent =
  | "INITIAL_SESSION"
  | "SIGNED_IN"
  | "SIGNED_OUT"
  | "TOKEN_REFRESHED"
  | "USER_UPDATED"
  | "PASSWORD_RECOVERY";

export interface AuthGateway {
  getConfiguration(): Promise<AuthConfiguration>;
  startOAuth(provider: AuthProvider): Promise<OAuthStart>;
  submitEmail(email: string): Promise<EmailLinkAccepted>;
  resendVerification(email: string): Promise<EmailLinkAccepted>;
  requestSignInOtp(email: string): Promise<EmailOtpAccepted>;
  verifySignInOtp(email: string, token: string): Promise<AuthUser>;
  verifyEmail(token: string): Promise<AuthUser>;
  completeCallback(code: string): Promise<AuthUser>;
  completeInvite(accessToken: string, refreshToken: string): Promise<AuthUser>;
  startAlpha(email: string): Promise<AlphaAdmissionStart>;
  verifyAlphaOtp(email: string, token: string): Promise<AlphaAdmissionComplete>;
  getMe(): Promise<AuthUser | null>;
  logout(): Promise<void>;
  subscribe(listener: (event: AuthSessionEvent) => void): () => void;
}

export const authGateway: AuthGateway = {
  async getConfiguration() {
    const configuration = await apiRequest<AuthConfiguration>("/auth/config");
    if (configuration.authMode !== browserAuthMode()) {
      throw clientAuthProblem(
        "auth_mode_mismatch",
        "The web and API authentication modes do not match.",
      );
    }
    return configuration;
  },

  async startOAuth(provider) {
    if (browserAuthMode() === "local") {
      return apiRequest<OAuthStart>(`/auth/oauth/${provider}`, {
        method: "POST",
        body: JSON.stringify({}),
      });
    }
    const configuration = await authGateway.getConfiguration();
    const enabled =
      provider === "google"
        ? configuration.googleEnabled
        : configuration.linkedinEnabled;
    if (!enabled) {
      throw clientAuthProblem(
        "provider_unavailable",
        `${provider === "google" ? "Google" : "LinkedIn"} sign-in is not available.`,
      );
    }
    const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
      provider: provider === "google" ? "google" : "linkedin_oidc",
      options: {
        redirectTo: authCallbackUrl(),
        skipBrowserRedirect: true,
      },
    });
    if (error || !data.url) {
      throw clientAuthProblem(
        "provider_unavailable",
        "The authentication provider is not available right now.",
      );
    }
    return { redirectUrl: data.url };
  },

  async submitEmail(email) {
    if (browserAuthMode() === "local") {
      return apiRequest<EmailLinkAccepted>("/auth/email", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    }
    const configuration = await authGateway.getConfiguration();
    if (!configuration.emailEnabled) {
      throw clientAuthProblem(
        "email_auth_unavailable",
        "Email sign-in is not available right now.",
      );
    }
    const { error } = await getSupabaseClient().auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: authCallbackUrl(),
        shouldCreateUser: false,
      },
    });
    if (error) {
      throw supabaseEmailProblem(error);
    }
    return { accepted: true };
  },

  async resendVerification(email) {
    if (browserAuthMode() === "local") {
      return apiRequest<EmailLinkAccepted>("/auth/resend", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    }
    return authGateway.submitEmail(email);
  },

  async requestSignInOtp(email) {
    if (browserAuthMode() !== "supabase") {
      throw clientAuthProblem(
        "email_auth_unavailable",
        "Six-digit email sign-in requires Supabase authentication.",
      );
    }
    const configuration = await authGateway.getConfiguration();
    if (!configuration.emailEnabled) {
      throw clientAuthProblem(
        "email_auth_unavailable",
        "Email sign-in is not available right now.",
      );
    }
    try {
      return await apiRequest<EmailOtpAccepted>("/auth/alpha/signin", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      // Never fall back to Supabase's browser-side signInWithOtp here. That
      // path can send a signup/magic-link message instead of the six-digit
      // server-mediated OTP contract. A stale deployment must fail visibly so
      // it can be updated, rather than silently creating the wrong auth flow.
      if (error instanceof ApiClientError && error.problem.status === 404) {
        throw clientAuthProblem(
          "email_auth_unavailable",
          "Email sign-in is not available right now. Please try again shortly.",
          503,
        );
      }
      throw error;
    }
  },

  async verifySignInOtp(email, token) {
    if (browserAuthMode() !== "supabase") {
      throw clientAuthProblem(
        "email_auth_unavailable",
        "Six-digit email sign-in requires Supabase authentication.",
      );
    }
    const { error } = await getSupabaseClient().auth.verifyOtp({
      email,
      token,
      type: "email",
    });
    if (error) {
      throw clientAuthProblem(
        "signin_code_unavailable",
        "This six-digit code is invalid or expired.",
        401,
      );
    }
    try {
      return await loadHostedAccount();
    } catch (accountError) {
      if (
        accountError instanceof ApiClientError &&
        [401, 403, 429].includes(accountError.problem.status)
      ) {
        await getSupabaseClient()
          .auth.signOut({ scope: "local" })
          .catch(() => undefined);
        invalidateQueries();
      }
      throw accountError;
    }
  },

  verifyEmail(token) {
    if (browserAuthMode() !== "local") {
      return Promise.reject(
        clientAuthProblem(
          "invalid_callback",
          "Use the secure Supabase callback to finish sign-in.",
        ),
      );
    }
    return apiRequest<AuthUser>("/auth/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  },

  async completeCallback(code) {
    if (browserAuthMode() !== "supabase") {
      throw clientAuthProblem(
        "invalid_callback",
        "This callback is not available in local authentication mode.",
      );
    }
    const { error } =
      await getSupabaseClient().auth.exchangeCodeForSession(code);
    if (error) {
      throw clientAuthProblem(
        "invalid_callback",
        "This sign-in link is invalid or expired.",
      );
    }
    return loadHostedAccount();
  },

  async completeInvite(accessToken, refreshToken) {
    if (browserAuthMode() !== "supabase") {
      throw clientAuthProblem(
        "invalid_callback",
        "This callback is not available in local authentication mode.",
      );
    }
    const { error } = await getSupabaseClient().auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) {
      throw clientAuthProblem(
        "invalid_callback",
        "This Alpha invitation is invalid or expired.",
      );
    }
    return loadHostedAccount();
  },

  async startAlpha(email) {
    if (browserAuthMode() !== "supabase") {
      throw clientAuthProblem(
        "alpha_admission_unavailable",
        "Alpha code admission requires hosted authentication.",
      );
    }
    return apiRequest<AlphaAdmissionStart>("/auth/alpha/start", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  async verifyAlphaOtp(email, token) {
    if (browserAuthMode() !== "supabase") {
      throw clientAuthProblem(
        "alpha_admission_unavailable",
        "Alpha code verification requires hosted authentication.",
      );
    }
    const { error } = await getSupabaseClient().auth.verifyOtp({
      email,
      token,
      type: "email",
    });
    if (error) {
      throw clientAuthProblem(
        "alpha_code_unavailable",
        "This six-digit code is invalid or expired.",
        401,
      );
    }
    const result = await apiRequest<AlphaAdmissionComplete>(
      "/auth/alpha/complete",
      { method: "POST", body: JSON.stringify({}) },
    );
    if (result.state === "admitted") await loadHostedAccount();
    return result;
  },

  async getMe() {
    return cachedQuery(
      "auth:me",
      async (signal) => {
        try {
          return await apiRequest<AuthUser>("/me", { signal });
        } catch (error) {
          if (error instanceof ApiClientError && error.problem.status === 401) {
            return null;
          }
          throw error;
        }
      },
      2_000,
    );
  },

  async logout() {
    try {
      if (browserAuthMode() === "local") {
        await apiRequest<void>("/auth/logout", { method: "POST" });
        return;
      }
      const { error } = await getSupabaseClient().auth.signOut({
        scope: "local",
      });
      if (error) {
        throw clientAuthProblem(
          "signout_unavailable",
          "The browser session could not be cleared safely.",
        );
      }
    } finally {
      invalidateQueries();
    }
  },

  subscribe(listener) {
    try {
      return subscribeToSupabaseAuth((event) => {
        listener(event as AuthSessionEvent);
      });
    } catch (error) {
      if (error instanceof BrowserAuthConfigurationError) return () => {};
      throw error;
    }
  },
};

async function loadHostedAccount(): Promise<AuthUser> {
  invalidateQueries("auth:");
  const user = await authGateway.getMe();
  if (!user) {
    throw clientAuthProblem(
      "account_bootstrap_unavailable",
      "Your account could not be loaded safely.",
    );
  }
  return user;
}

function supabaseEmailProblem(error: {
  code?: string;
  status?: number;
}): ApiClientError {
  if (error.code === "over_email_send_rate_limit") {
    return clientAuthProblem(
      "email_rate_limited",
      "Email sign-in has reached its sending limit. Wait before requesting another code.",
      429,
    );
  }
  if (error.code === "over_request_rate_limit" || error.status === 429) {
    return clientAuthProblem(
      "email_request_rate_limited",
      "Too many sign-in requests were made. Wait a few minutes before retrying.",
      429,
    );
  }
  if (error.code === "email_address_not_authorized") {
    return clientAuthProblem(
      "email_address_not_authorized",
      "This Alpha email service cannot deliver to this address yet. Contact the Alpha operator.",
    );
  }
  if (error.code === "otp_disabled") {
    return clientAuthProblem(
      "email_auth_unavailable",
      "Email sign-in is not available right now.",
    );
  }
  return clientAuthProblem(
    "email_delivery_unavailable",
    "The sign-in email could not be sent. Retry shortly.",
  );
}

function clientAuthProblem(
  code: string,
  detail: string,
  status = 0,
): ApiClientError {
  return new ApiClientError({
    type: "about:blank",
    title: "Authentication unavailable",
    status,
    detail,
    code,
    correlationId: null,
  });
}
